import {
  getProductConfig,
  type QuoteWizardFlow,
} from '@/app/config/products.config';
import { BrokerInactiveNotice } from '@/features/brokers/components/broker-inactive-notice.component';
import { useSelectedBrokerWriteGuard } from '@/features/brokers/hooks';
import { useQuoteParams } from '@/features/quotes/context/quote-params.context';
import { useRdProposal } from '@/features/quotes/hooks/rd-equipamentos/use-rd-proposal.hook';
import type { RdStatus } from '@/services/quotes/rd-equipamentos/types';
import { useRequirePermissions } from '@/shared/hooks/use-require-permissions.hook';
import { Page, PageHeader } from '@/shared/ui/page';
import { Tag } from '@noorden-insurtech/alba-design-system';
import { useParams } from '@tanstack/react-router';
import { Home } from 'lucide-react';
import { useEffect } from 'react';
import { NewRdEquipamentosForm } from './new-rd-equipamentos-form.component';
import type { BrokerProductCategory } from '@/services/brokers/types';

type Props = {
  productId: string;
  category: string;
  productCode: string;
  stepCode: string;
  quoteFlow?: QuoteWizardFlow;
};

const RD_STATUS_CONFIG: Record<
  RdStatus,
  { label: string; colorScheme: 'gray' | 'yellow' | 'success' | 'error' }
> = {
  rascunho: { label: 'Rascunho', colorScheme: 'gray' },
  cotada: { label: 'Cotada', colorScheme: 'yellow' },
  aguardando_analise: { label: 'Aguardando Análise', colorScheme: 'yellow' },
  aprovada: { label: 'Aprovada', colorScheme: 'success' },
  aceita: { label: 'Aceita', colorScheme: 'success' },
  emitida: { label: 'Emitida', colorScheme: 'success' },
  cancelada: { label: 'Cancelada', colorScheme: 'error' },
};

export function NewRdEquipamentosLayout({
  productId,
  category,
  productCode,
  stepCode,
  quoteFlow = 'new',
}: Props) {
  const { id, policyId, setParams } = useQuoteParams();
  const params = useParams({ strict: false });
  // O wizard editável fica atrás do BrokerWriteGate; aqui o aviso vale para o
  // modo visualização, onde aceitar/aprovar/cancelar/baixar seguem na tela.
  const quoteActionGuard = useSelectedBrokerWriteGuard('quote', 'other');

  // Defesa em profundidade: a rota `_app/quotes/products/new` já bloqueia o
  // acesso via `beforeLoad` (`requirePermissions`); aqui reforçamos o
  // wizard em modo somente leitura caso a permissão seja revogada durante
  // a navegação entre steps (SPA, sem reload da rota).
  const { hasPermission: canWriteQuote } = useRequirePermissions({
    permissions: ['policies.quotes.write'],
  });

  const resolvedProposalId = (id || policyId || '').trim();
  const { data: proposal } = useRdProposal(resolvedProposalId || undefined);

  useEffect(() => {
    setParams({
      productId,
      // `category` chega como string crua do search da rota; o contexto guarda
      // a união de BrokerProductCategory.
      category: category as BrokerProductCategory,
      productCode,
      ...(params.id && {
        id: params.id as string,
        policyId: params.id as string,
      }),
    });
  }, [productId, category, productCode, params.id, setParams]);

  const displayName = getProductConfig(productCode)?.displayName ?? productCode;
  /**
   * O rótulo segue o ESTADO, não o parâmetro de URL: uma proposta que já tem
   * número é, por definição, uma cotação existente. A lista de Cotações abre
   * o wizard com `quoteFlow=new` fixo, o que produzia o título contraditório
   * "Nova Cotação RD Equipamentos - Nº 0171-0021". Mesma lição do modo
   * leitura (que derivava de `quoteFlow` e deixava uma proposta emitida
   * editável).
   */
  const isViewChrome = quoteFlow === 'view';
  const isEditChrome =
    quoteFlow === 'edit' || (!isViewChrome && !!proposal?.number?.trim());
  const effectiveQuoteFlow: QuoteWizardFlow =
    canWriteQuote === false ? 'view' : quoteFlow;

  const proposalNumberLabel = proposal?.number?.trim() || '';

  const title = isEditChrome
    ? 'Editar Cotação'
    : isViewChrome
      ? proposalNumberLabel
        ? `Cotação ${displayName} - Nº ${proposalNumberLabel}`
        : `Cotação ${displayName}`
      : proposalNumberLabel
        ? `Nova Cotação ${displayName} - Nº ${proposalNumberLabel}`
        : `Nova Cotação ${displayName}`;

  const subtitle = isEditChrome
    ? 'Edite as informações da cotação'
    : isViewChrome
      ? 'Visualize as informações da cotação'
      : 'Preencha as informações para calcular o seguro';

  const breadcrumbItems = [
    {
      label: 'Página Inicial',
      href: '/dashboard',
      iconLeft: <Home className='h-4 w-4' />,
    },
    { label: 'Cotações', href: '/quotes/products' },
    { label: 'Nova cotação', href: '/quotes/products/new' },
    { label: displayName, active: true },
  ];

  const statusConfig = proposal?.status
    ? RD_STATUS_CONFIG[proposal.status]
    : undefined;

  return (
    <Page.Root className='h-auto lg:h-full'>
      <Page.Breadcrumb items={breadcrumbItems} />

      <PageHeader.Root>
        <PageHeader.Main>
          <PageHeader.TitleGroup>
            <PageHeader.Title>{title}</PageHeader.Title>
            <PageHeader.Subtitle>{subtitle}</PageHeader.Subtitle>
          </PageHeader.TitleGroup>
          {statusConfig ? (
            <PageHeader.Actions>
              <Tag.Root
                variant='outline'
                colorScheme={statusConfig.colorScheme}
                size='sm'
              >
                <Tag.Label>{statusConfig.label}</Tag.Label>
              </Tag.Root>
            </PageHeader.Actions>
          ) : null}
        </PageHeader.Main>
      </PageHeader.Root>

      <BrokerInactiveNotice guards={[quoteActionGuard]} />

      <Page.Content className='py-4'>
        <NewRdEquipamentosForm
          productId={productId}
          category={category}
          productCode={productCode}
          stepCode={stepCode}
          quoteFlow={effectiveQuoteFlow}
        />
      </Page.Content>
    </Page.Root>
  );
}
